import { useState, useEffect } from 'react';
import { Form, Button, Spinner, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faWrench, faClock, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { collection, getDocs, orderBy, query, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuthContext } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../utils/formatCurrency';
import { getRepairTypeLabel } from './repairTypes';

export default function RepairCalcArchive({ onSelectQuote, onImportQuote }) {
  const { t } = useTranslation();
  const { currentUser: user } = useAuthContext();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);

  useEffect(() => {
    async function loadQuotes() {
      if (!user) return;
      try {
        const q = query(
          collection(db, 'users', user.uid, 'quotes'),
          orderBy('updatedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const results = [];
        querySnapshot.forEach((d) => {
          results.push({ id: d.id, ...d.data() });
        });
        setQuotes(results);
      } catch (error) {
        console.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    }
    loadQuotes();
  }, [user]);

  const filteredQuotes = quotes.filter((q) =>
    decodeURIComponent(q.id).toLowerCase().includes(search.toLowerCase())
  );

  const shownQuotes = filteredQuotes.slice(0, visibleCount);

  const handleConfirmDelete = async (id) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'quotes', id));
      setQuotes(quotes.filter((q) => q.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting quote:', error);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="repaircalc-section">
      <h5 className="mb-3">{t('repaircalc.archive.title')}</h5>

      <Form.Group className="mb-3">
        <div className="input-group">
          <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
          <Form.Control
            type="text"
            placeholder={t('repaircalc.archive.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(50); }}
          />
        </div>
      </Form.Group>

      {loading ? (
        <div className="text-center py-4 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          {t('repaircalc.archive.loading')}
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="text-center py-4 text-muted">
          {search ? t('repaircalc.archive.noResults') : t('repaircalc.archive.noQuotes')}
        </div>
      ) : (
        <div className="row g-3">
          {shownQuotes.map((quote) => {
            const title = quote.brand
              ? `${quote.brand} ${quote.modelName}`
              : (quote.modelName || decodeURIComponent(quote.id).split(' - ')[0]);
            return (
              <div className="col-12 col-md-6" key={quote.id}>
                <Card
                  className="h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelectQuote(quote)}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{title}</h6>
                        {quote.repairType && (
                          <div className="small text-muted">
                            <FontAwesomeIcon icon={faWrench} className="me-1" />
                            {getRepairTypeLabel(quote.repairType, t)}
                          </div>
                        )}
                      </div>
                      {deleteConfirm === quote.id ? (
                        <div className="d-flex align-items-center gap-2">
                          <span className="small text-danger fw-medium">{t('repaircalc.archive.deleteConfirm')}</span>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={(e) => { e.stopPropagation(); handleConfirmDelete(quote.id); }}
                          >
                            {t('repaircalc.archive.yes')}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                          >
                            {t('repaircalc.archive.no')}
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="btn-icon"
                            title={t('repaircalc.calculator.importToDevice')}
                            onClick={(e) => { e.stopPropagation(); onImportQuote(quote); }}
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="btn-icon"
                            title={t('repaircalc.archive.delete')}
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(quote.id); }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-end mt-3 pt-3 border-top">
                      <div>
                        <div className="small text-muted">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {new Date(quote.updatedAt).toLocaleDateString()}
                        </div>
                        <div className="small">
                          {t('repaircalc.archive.total')}: <strong>{formatCurrency(quote.totalClient, 'EUR')}</strong>
                        </div>
                      </div>
                      <div className="text-profit fw-medium">
                        {t('repaircalc.archive.net')}: {formatCurrency(quote.netProfit, 'EUR')}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            );
          })}

          {filteredQuotes.length > visibleCount && (
            <div className="col-12 text-center mt-2">
              <Button
                variant="outline-primary"
                onClick={() => setVisibleCount((v) => v + 50)}
              >
                {t('repaircalc.archive.loadMore')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
